import {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
} from 'axios';
import * as https from 'https';

import { getAxiosWithDebugLogger } from '../utils/logger';

/**
 * Client for making HTTP requests using TypeScript only
 */
export class RequestClient {
  private baseUrl: string;

  private agent: https.Agent;

  private baseOptions: AxiosRequestConfig;

  private axiosInstance: AxiosInstance;

  constructor(baseUrl: string, enableDebug = false, private apiKey?: string) {
    this.baseUrl = baseUrl;
    this.agent = new https.Agent();
    this.axiosInstance = getAxiosWithDebugLogger(enableDebug);

    this.baseOptions = {
      baseURL: this.baseUrl,
      httpsAgent: this.agent,
      headers: {
        'Content-Type': 'application/json',
      },
    };

    // If provided, add our api key to all request headers.
    if (apiKey) {
      this.baseOptions.headers['x-api-key'] = this.apiKey;
    }
  }

  /**
   * Request configuration
   */
  public replaceApiVersion(path: string, version: string): string {
    return path.replace(/\/v[0-9]+$/, `/${version}`);
  }

  public addHeaders(
    options: AxiosRequestConfig,
    headers: { [key: string]: string },
  ): AxiosRequestConfig {
    return {
      ...options,
      headers: {
        ...options.headers,
        ...headers,
      },
    };
  }

  public addParams(
    options: AxiosRequestConfig,
    params: any,
  ): AxiosRequestConfig {
    return {
      ...options,
      params: {
        ...options.params,
        ...params,
      },
    };
  }

  /**
   * Response configuration
   */
  private handleResponse<T>(response: AxiosResponse<T>): T {
    return response.data;
  }

  private handleErrors(error: AxiosError): any {
    if (error.response?.data) {
      throw error.response?.data;
    }
    throw error;
  }

  /**
   * HTTP methods
   */
  public async get(
    path: string,
    params?: any,
    headers?: { [key: string]: string },
    version?: string,
  ): Promise<unknown> {
    let options = this.baseOptions;

    if (version && options.baseURL) {
      options.baseURL = this.replaceApiVersion(options.baseURL, version);
    }

    if (params) {
      options = this.addParams(options, params);
    }

    if (headers) {
      options = this.addHeaders(options, headers);
    }

    try {
      const response = await this.axiosInstance.get(`${path}`, options);
      return this.handleResponse(response);
    } catch (error) {
      return this.handleErrors(error as AxiosError<unknown>);
    }
  }

  public async post(
    path: string,
    data: any,
    headers?: { [key: string]: string },
    version?: string,
  ): Promise<unknown> {
    let options = this.baseOptions;

    if (version && options.baseURL) {
      options.baseURL = this.replaceApiVersion(options.baseURL, version);
    }

    if (headers) {
      options = this.addHeaders(options, headers);
    }

    try {
      const response = await this.axiosInstance.post(`${path}`, data, options);
      return this.handleResponse(response);
    } catch (error) {
      return this.handleErrors(error as AxiosError<unknown>);
    }
  }

  public async patch(
    path: string,
    data: any,
    headers?: { [key: string]: string },
    version?: string,
  ): Promise<unknown> {
    let options = this.baseOptions;

    if (version && options.baseURL) {
      options.baseURL = this.replaceApiVersion(options.baseURL, version);
    }

    if (headers) {
      options = this.addHeaders(options, headers);
    }

    try {
      const response = await this.axiosInstance.patch(`${path}`, data, options);
      return this.handleResponse(response);
    } catch (error) {
      return this.handleErrors(error as AxiosError<unknown>);
    }
  }

  public async delete(path: string, data: any): Promise<unknown> {
    const options = {
      ...this.baseOptions,
      data,
    };

    try {
      const response = await this.axiosInstance.delete(`${path}`, options);
      return this.handleResponse(response);
    } catch (error) {
      return this.handleErrors(error as AxiosError<unknown>);
    }
  }
}
